import React, { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import {
    Col,
    Row,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Input,
    FormFeedback,
    Label,
    Card,
    CardBody,
    Button,
    UncontrolledTooltip,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"

// Custom Components
import TableContainer from "components/Common/TableContainer"
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Hooks
import {
    useAuctionGrades,
    useCreateAuctionGrade,
    useEditAuctionGrade,
    useDeleteAuctionGrade,
} from "hooks/auctionGradeHook"
import { useListParams } from "hooks/useListParams"
import { formatReadableDate } from "utils/formatDate"

const AuctionGradeList = () => {
  document.title = "Auction Grade List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [grade, setGrade] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  )

  const {
    data: gradesResponse,
    isLoading,
    refetch: gradeRefetch,
    isRefetching: gradeRefetchLoading,
  } = useAuctionGrades(queryParams)

  const { mutateAsync: createGrade, isPending: createLoading } =
    useCreateAuctionGrade()
  const { mutateAsync: editGrade, isPending: editLoading } =
    useEditAuctionGrade()
  const { mutateAsync: deleteGrade, isPending: deleteLoading } =
    useDeleteAuctionGrade()

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setGrade(null)
      setIsEdit(false)
    } else setModal(true)
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setGrade(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleGradeClick = useCallback(
    arg => {
      setGrade({ id: arg.id, name: arg.name })
      setIsEdit(true)
      toggle()
    },
    [toggle]
  )

  const onClickDelete = useCallback(gradeData => {
    setGrade(gradeData)
    setDeleteModal(true)
  }, [])

  const handleDeleteGrade = useCallback(async () => {
    if (grade?.id) {
      try {
        const res = await deleteGrade(grade.id)
        toast.success(res?.message ?? "Grade deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        console.error(error)
        toast.error("Failed to delete grade.")
      }
    }
  }, [grade, deleteGrade])

  const handleListRefresh = () => gradeRefetch()

  const validationSchema = Yup.object({
    name: Yup.string().trim().required("Please enter grade name"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const payload = { name: values.name }
        const action = isEdit
          ? editGrade({ id: grade.id, ...payload })
          : createGrade(payload)
        const res = await action
        toast.success(
          res?.message ??
            `Grade ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggle()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save grade.")
      }
    },
    [isEdit, grade, toggle, editGrade, createGrade]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { name: grade?.name || "" },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Slug",
        accessorKey: "slug",
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-info"
                onClick={() => handleGradeClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </button>
            </li>
          </ul>
        ),
      },
    ],
    [handleGradeClick, onClickDelete]
  )

  const gradesData = gradesResponse?.data?.data || []
  const paginationInfo = gradesResponse?.data

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteGrade}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title="Auction Grade"
            breadcrumbItem="Auction Grade Lists"
          />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 flex-grow-1">Auction Grade Lists</h5>
                    <div className="flex-shrink-0">
                      <Button color="primary" onClick={handleAddNewClick}>
                        Add New Grade
                      </Button>
                      <Button
                        color="light"
                        onClick={handleListRefresh}
                        className="ms-1"
                        id="refreshTooltip"
                      >
                        <i
                          className={`mdi mdi-refresh ${
                            gradeRefetchLoading ? "mdi-spin" : ""
                          }`}
                        />
                        <UncontrolledTooltip
                          placement="top"
                          target="refreshTooltip"
                        >
                          Refresh
                        </UncontrolledTooltip>
                      </Button>

                      {hasActiveFilters && (
                        <Button
                          color="danger"
                          onClick={resetParams}
                          className="ms-1"
                        >
                          Reset Filters
                        </Button>
                      )}
                    </div>
                  </div>
                </CardBody>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={gradesData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || gradeRefetchLoading}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle}>
              {isEdit ? "Edit Grade" : "Add Grade"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={e => {
                  e.preventDefault()
                  validation.handleSubmit()
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="name">Grade Name</Label>
                  <Input
                    name="name"
                    id="name"
                    type="text"
                    placeholder="Enter grade name"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name || ""}
                    invalid={
                      validation.touched.name && !!validation.errors.name
                    }
                  />
                  {validation.touched.name && validation.errors.name && (
                    <FormFeedback type="invalid">
                      {validation.errors.name}
                    </FormFeedback>
                  )}
                </div>
                <div className="text-end">
                  <Button
                    color="success"
                    type="submit"
                    disabled={editLoading || createLoading}
                  >
                    {isEdit ? "Save Changes" : "Add Grade"}
                    {(editLoading || createLoading) && "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default AuctionGradeList
